import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useDiscogsUser } from '../../context/DiscogsContext'
import { useCurrentUserProfile, userKeys } from '../../hooks/useTracking'
import { completeOnboarding } from '../../api/tracking'
import {
  CHAPTERS,
  visibleChapters,
  isFoundationComplete,
  nextFoundationChapter,
  type TourCtx,
  type TourChapter,
} from './chapters'
import { WelcomeCard } from './WelcomeCard'
import { TourPill } from './TourPill'
import { ChapterPlayer } from './ChapterPlayer'
import { CompletionBeat } from './CompletionBeat'
import styles from './Tour.module.css'

const STATE_KEY = 'tenwords:tour-state-v3'
const LEGACY_KEYS = ['tenwords:tour-index', 'tenwords:tour-state-v2']
export const TOUR_DEBUG_KEY = 'tenwords:tour-debug'

interface TourState {
  welcomeSeen: boolean
  /** When the user clicks "Take 30 seconds first" — defers next display. */
  deferredUntil: number | null
  activeChapter: string | null
  activeStep: number
  /** Last chapter that was just completed — shows the beat card. */
  beatFor: string | null
  completed: Record<string, boolean>
  /** Foundation must finish before this can be set. */
  dismissed: boolean
}

const INITIAL: TourState = {
  welcomeSeen: false,
  deferredUntil: null,
  activeChapter: null,
  activeStep: 0,
  beatFor: null,
  completed: {},
  dismissed: false,
}

function loadState(): TourState {
  try {
    const raw = localStorage.getItem(STATE_KEY)
    if (!raw) return INITIAL
    const parsed = JSON.parse(raw)
    return { ...INITIAL, ...parsed, completed: parsed.completed ?? {} }
  } catch {
    return INITIAL
  }
}

function isDebug(): boolean {
  try { return localStorage.getItem(TOUR_DEBUG_KEY) === '1' } catch { return false }
}

export function Tour() {
  const { data: profile, refetch } = useCurrentUserProfile()
  const discogsUser = useDiscogsUser()
  const queryClient = useQueryClient()

  const [state, setState] = useState<TourState>(INITIAL)
  const [hydrated, setHydrated] = useState(false)
  const [revealed, setRevealed] = useState(false)

  const ctx: TourCtx = useMemo(
    () => ({ discogsConnected: !!discogsUser }),
    [discogsUser]
  )
  const chapters = useMemo(() => visibleChapters(ctx), [ctx])
  const foundationDone = useMemo(
    () => isFoundationComplete(ctx, state.completed),
    [ctx, state.completed]
  )

  // ── Hydrate from localStorage once ────────────────────────────────
  useEffect(() => {
    setState(loadState())
    setHydrated(true)
    // Clean up legacy keys
    LEGACY_KEYS.forEach(k => {
      try { localStorage.removeItem(k) } catch { /* ignore */ }
    })
  }, [])

  // ── Persist state changes ─────────────────────────────────────────
  useEffect(() => {
    if (!hydrated) return
    try { localStorage.setItem(STATE_KEY, JSON.stringify(state)) } catch { /* ignore */ }
  }, [state, hydrated])

  // ── Gate first reveal: defer if user picked "30 seconds first" ────
  useEffect(() => {
    if (!hydrated) return
    if (!profile) return
    if (profile.onboarding_completed_at && !isDebug()) return
    if (state.dismissed && !isDebug()) return
    if (revealed) return

    const now = Date.now()
    if (state.deferredUntil && state.deferredUntil > now) {
      const wait = state.deferredUntil - now
      const t = setTimeout(() => setRevealed(true), wait)
      return () => clearTimeout(t)
    }

    const delay = state.welcomeSeen ? 600 : 1800
    const t = setTimeout(() => setRevealed(true), delay)
    return () => clearTimeout(t)
  }, [hydrated, profile?.id, profile?.onboarding_completed_at, state.welcomeSeen, state.dismissed, state.deferredUntil, revealed])

  // ── Mark onboarding complete on the backend ──────────────────────
  const markServerComplete = useCallback(async () => {
    if (isDebug()) return
    try { await completeOnboarding() } catch { /* ignore */ }
    refetch?.()
    queryClient.invalidateQueries({ queryKey: userKeys.me })
  }, [refetch, queryClient])

  // ── Foundation completion triggers server onboarding mark ────────
  useEffect(() => {
    if (!hydrated || !profile) return
    if (profile.onboarding_completed_at) return
    if (foundationDone) markServerComplete()
  }, [hydrated, profile, foundationDone, markServerComplete])

  // ── Drop active chapter if it became invisible (e.g. skipIf) ─────
  useEffect(() => {
    if (!state.activeChapter) return
    const stillVisible = chapters.some(c => c.id === state.activeChapter)
    if (!stillVisible) setState(s => ({ ...s, activeChapter: null }))
  }, [state.activeChapter, chapters])

  // ── Action handlers ──────────────────────────────────────────────
  const seeWelcome = useCallback(() => {
    setState(s => ({ ...s, welcomeSeen: true, deferredUntil: null }))
  }, [])

  const deferWelcome = useCallback(() => {
    // Hide for 30 seconds, then re-show the welcome card
    setState(s => ({ ...s, deferredUntil: Date.now() + 30_000 }))
    setRevealed(false)
  }, [])

  const dismissTour = useCallback(() => {
    setState(s => ({ ...s, dismissed: true, activeChapter: null, beatFor: null }))
  }, [])

  const pickChapter = useCallback((id: string) => {
    setState(s => ({ ...s, activeChapter: id, activeStep: 0, beatFor: null, welcomeSeen: true }))
  }, [])

  const pauseChapter = useCallback(() => {
    setState(s => ({ ...s, activeChapter: null }))
  }, [])

  const advanceStep = useCallback(() => {
    setState(s => ({ ...s, activeStep: s.activeStep + 1 }))
  }, [])

  const completeActiveChapter = useCallback(() => {
    setState(s => {
      if (!s.activeChapter) return s
      return {
        ...s,
        completed: { ...s.completed, [s.activeChapter]: true },
        beatFor: s.activeChapter,
        activeChapter: null,
        activeStep: 0,
      }
    })
  }, [])

  const dismissBeat = useCallback(() => {
    setState(s => ({ ...s, beatFor: null }))
  }, [])

  // ── Render decision ──────────────────────────────────────────────
  if (!hydrated || !profile) return null
  if (profile.onboarding_completed_at && !isDebug() && state.dismissed) return null
  // Note: we DON'T return null based purely on onboarding_completed_at —
  // the user can still complete discovery chapters; the pill stays until
  // they dismiss or finish everything.
  if (!revealed) return null

  // Welcome card phase — only on first ever visit
  if (!state.welcomeSeen) {
    return createPortal(
      <div className={styles.root}>
        <WelcomeCard onStart={() => {
          seeWelcome()
          // Auto-start the first foundation chapter
          const next = nextFoundationChapter(ctx, state.completed)
          if (next) pickChapter(next.id)
        }} onDefer={deferWelcome} />
      </div>,
      document.body
    )
  }

  // Completion beat — between chapters
  const beatChapter: TourChapter | null = state.beatFor
    ? (CHAPTERS.find(c => c.id === state.beatFor) ?? null)
    : null

  if (beatChapter) {
    const beatIsFoundation = beatChapter.phase === 'foundation'
    const nextFound = nextFoundationChapter(ctx, state.completed)
    // For foundation chapters: next is the next foundation chapter (or null when foundation done)
    // For discovery: no chain — beat just acknowledges
    const nextCh = beatIsFoundation ? nextFound : null
    const justFinishedFoundation = beatIsFoundation && !nextFound

    return createPortal(
      <div className={styles.root}>
        <CompletionBeat
          chapter={beatChapter}
          nextChapter={nextCh}
          isFoundationDone={justFinishedFoundation}
          onContinue={() => {
            dismissBeat()
            if (nextCh) pickChapter(nextCh.id)
          }}
          onPause={dismissBeat}
        />
        {/* Pill stays visible behind the beat dim */}
        <TourPill
          ctx={ctx}
          completed={state.completed}
          activeChapterId={state.activeChapter}
          foundationDone={foundationDone}
          onPickChapter={pickChapter}
          onDismiss={dismissTour}
        />
      </div>,
      document.body
    )
  }

  const activeChapter = state.activeChapter
    ? chapters.find(c => c.id === state.activeChapter) ?? null
    : null

  const safeStepIndex = activeChapter
    ? Math.min(state.activeStep, activeChapter.steps.length - 1)
    : 0

  return createPortal(
    <div className={styles.root}>
      {activeChapter && (
        <ChapterPlayer
          chapter={activeChapter}
          stepIndex={safeStepIndex}
          onAdvance={advanceStep}
          onCompleteChapter={completeActiveChapter}
          onPause={pauseChapter}
          onPickAnother={pauseChapter}
        />
      )}
      <TourPill
        ctx={ctx}
        completed={state.completed}
        activeChapterId={state.activeChapter}
        foundationDone={foundationDone}
        onPickChapter={pickChapter}
        onDismiss={dismissTour}
      />
    </div>,
    document.body
  )
}
